/**
 * Chrome/Edge 151+ `<usermedia>` (and 153 `<camera>`/`<microphone>`) as a
 * permission broker only. After Block, a tap on the UA element can recover
 * in-page. We stop the element's tracks and hand off to LiveKit / probes.
 *
 * Do not detect via `HTMLUserMediaElement in window` alone — Chrome ships the
 * tag without putting that constructor on window. Probe createElement instead.
 */

export type CapabilityKind = 'mic' | 'cam'
export type CapabilityTag = 'microphone' | 'camera' | 'usermedia'

type BrokerProbe = {
  setConstraints?: (constraints: MediaStreamConstraints) => void
  stream?: MediaStream | null
  track?: MediaStreamTrack | null
}

export function brokerTagWorks(
  tag: CapabilityTag,
  create?: (name: string) => BrokerProbe,
): boolean {
  try {
    const el = create
      ? create(tag)
      : typeof document === 'undefined'
        ? null
        : (document.createElement(tag) as unknown as BrokerProbe)
    if (!el) return false
    return typeof el.setConstraints === 'function' || 'stream' in el || 'track' in el
  } catch {
    return false
  }
}

export function capabilityTag(
  kind: CapabilityKind,
  host?: object,
  create?: (name: string) => BrokerProbe,
): CapabilityTag | null {
  const names = (host ?? (typeof globalThis === 'object' ? globalThis : {})) as Record<
    string,
    unknown
  >
  if (kind === 'mic' && 'HTMLMicrophoneElement' in names) return 'microphone'
  if (kind === 'cam' && 'HTMLCameraElement' in names) return 'camera'
  if ('HTMLUserMediaElement' in names) return 'usermedia'

  if (host && !create) return null

  if (kind === 'mic' && brokerTagWorks('microphone', create)) return 'microphone'
  if (kind === 'cam' && brokerTagWorks('camera', create)) return 'camera'
  if (brokerTagWorks('usermedia', create)) return 'usermedia'
  return null
}

export function capabilityConstraints(kind: CapabilityKind): MediaStreamConstraints {
  // Objects, not `false`. Combined <usermedia> defaults to both tracks; `video: false`
  // is treated as a required constraint and can error the element on mount.
  return kind === 'mic' ? { audio: {} } : { video: {} }
}

export function stopCapabilityStream(stream: MediaStream | null | undefined) {
  stream?.getTracks().forEach((track) => track.stop())
}
